// Home page FAQ copy, kept in its own dictionary rather than the giant strings.ts — same precedent
// as environmentCheckStrings.ts/leakStrings.ts. Locales without a full translation fall back to
// English via getHomeFaqStrings rather than shipping a partially-translated section.
//
// Structurally identical to (but not imported from) hooks/useFaqJsonLd.ts's FaqItem — that file
// touches the DOM, and this one is also imported from vite.config.ts's build-time SEO snapshot
// (see scripts/homeSeoSnapshot.ts), which is type-checked under tsconfig.node.json's Node/ES2023-only
// lib with no "DOM". Pulling in a DOM-touching module there would fail that build.
export interface HomeFaqItem {
  question: string;
  answer: string;
}

export interface HomeFaqStrings {
  title: string;
  items: HomeFaqItem[];
}

const en: HomeFaqStrings = {
  title: "Frequently asked questions",
  items: [
    {
      question: "What is an IP reputation check?",
      answer:
        "An IP reputation check looks at signals such as proxy, VPN, Tor, datacenter status, abuse reports and threat intelligence to describe how trustworthy an IP address's network activity looks.",
    },
    {
      question: "What does the NetRiskScan Index mean?",
      answer:
        "The NetRiskScan Index is a 0-100 score where higher means a cleaner network reputation: 90-100 is Excellent, 75-89 Good, 60-74 Fair, 40-59 Poor, and 0-39 High risk.",
    },
    {
      question: "Is a VPN IP always risky?",
      answer:
        "No. A VPN address does not automatically mean malicious traffic — many people use VPNs for ordinary privacy reasons. NetRiskScan reports VPN status as one signal among several, not a verdict on its own.",
    },
    {
      question: "Does a datacenter IP mean malicious traffic?",
      answer:
        "No. Datacenter infrastructure does not automatically mean high risk. Many legitimate services, including public DNS resolvers, run on datacenter IPs.",
    },
    {
      question: "Can NetRiskScan detect residential proxies?",
      answer:
        "Yes. Residential proxy is one of the network-type signals NetRiskScan reports alongside VPN, datacenter and hosting — based on ASN registration and network-type classification, not just IP location.",
    },
    {
      question: "How do I check whether an IP is a Tor exit node?",
      answer:
        "Tor exit-node status is one of the signals shown in every IP check result on this page. Enter or auto-detect an address and look for the Tor flag alongside VPN, proxy and datacenter.",
    },
    {
      question: "How can I check a VPS's IP reputation?",
      answer:
        "Check its public IP on this page the same way you would any address, or run netriskscan-cli or the Developer API directly from the VPS for automated, repeatable checks.",
    },
    {
      question: "Can I use NetRiskScan without an account?",
      answer: "Yes. You can check an IP address on this page without creating an account.",
    },
    {
      question: "Does NetRiskScan provide an API?",
      answer:
        "Yes. The NetRiskScan Developer API lets you integrate IP reputation and network intelligence directly into your application.",
    },
    {
      question: "Is there a CLI?",
      answer: "Yes. netriskscan-cli is an official command-line client you can run with npx, with no signup required for your first checks.",
    },
    {
      question: "Is there a JavaScript SDK?",
      answer: "Yes. @netriskscan/sdk is a typed JavaScript/TypeScript client for the Developer API.",
    },
    {
      question: "What does Unknown mean in a result?",
      answer:
        "Unknown means NetRiskScan does not have enough evidence to confirm or rule out a signal for that address. It is treated differently from a confirmed negative result, not assumed to be false.",
    },
  ],
};

const zhCn: HomeFaqStrings = {
  title: "常见问题",
  items: [
    {
      question: "什么是 IP 信誉检测？",
      answer: "IP 信誉检测通过代理、VPN、Tor、数据中心、滥用记录与威胁情报等信号，描述一个 IP 地址网络活动的可信程度。",
    },
    {
      question: "NetRiskScan Index 是什么意思？",
      answer: "NetRiskScan Index 是 0-100 的分数，分数越高代表网络信誉越干净：90-100 为极佳，75-89 为良好，60-74 为一般，40-59 为较差，0-39 为高风险。",
    },
    {
      question: "VPN 的 IP 一定有风险吗？",
      answer: "不一定。使用 VPN 不代表一定存在恶意流量，许多用户出于日常隐私需求使用 VPN。NetRiskScan 只把 VPN 状态作为多个信号之一，而非单独结论。",
    },
    {
      question: "数据中心 IP 代表恶意流量吗？",
      answer: "不代表。数据中心基础设施不代表一定是高风险，许多合法服务（包括公共 DNS 解析器）都运行在数据中心 IP 上。",
    },
    {
      question: "NetRiskScan 能检测住宅代理吗？",
      answer: "可以。住宅代理是 NetRiskScan 报告的网络类型信号之一，与 VPN、数据中心、托管等信号并列，依据 ASN 注册信息与网络类型分类判定，而不仅仅依据 IP 位置。",
    },
    {
      question: "如何检测一个 IP 是否为 Tor 出口节点？",
      answer: "Tor 出口节点状态是本页每次 IP 检测结果中都会展示的信号之一。输入或自动检测一个地址后，即可在结果中看到与 VPN、代理、数据中心并列的 Tor 标记。",
    },
    {
      question: "如何检测 VPS 的 IP 信誉？",
      answer: "可以像检测任何地址一样，在本页面检测 VPS 的公网 IP；也可以直接在 VPS 上运行 netriskscan-cli 或调用 Developer API，实现自动化、可重复的检测。",
    },
    {
      question: "不注册可以使用 NetRiskScan 吗？",
      answer: "可以。你可以在本页面直接检测 IP 地址，无需注册。",
    },
    {
      question: "NetRiskScan 提供 API 吗？",
      answer: "提供。NetRiskScan Developer API 可以让你将 IP 信誉与网络情报直接集成到你的应用中。",
    },
    {
      question: "有命令行工具（CLI）吗？",
      answer: "有。netriskscan-cli 是官方命令行客户端，可通过 npx 直接运行，前几次检测无需注册。",
    },
    {
      question: "有 JavaScript SDK 吗？",
      answer: "有。@netriskscan/sdk 是面向 Developer API 的类型化 JavaScript/TypeScript 客户端。",
    },
    {
      question: "结果中的“未知”是什么意思？",
      answer: "“未知”表示 NetRiskScan 没有足够证据确认或排除该地址的某个信号，这与已确认的否定结果会被区别对待，不会被当作“否”处理。",
    },
  ],
};

const zhTw: HomeFaqStrings = {
  title: "常見問題",
  items: [
    {
      question: "什麼是 IP 信譽檢測？",
      answer: "IP 信譽檢測透過代理、VPN、Tor、資料中心判定、濫用回報與威脅情報等訊號，描述一個 IP 位址網路活動的可信程度。",
    },
    {
      question: "NetRiskScan Index 是什麼意思？",
      answer: "NetRiskScan Index 是 0-100 的分數，數值越高代表網路信譽越乾淨：90-100 為極佳，75-89 為良好，60-74 為普通，40-59 為較差，0-39 為高風險。",
    },
    {
      question: "VPN 的 IP 一定有風險嗎？",
      answer: "不一定。VPN 位址不代表一定存在惡意流量，許多使用者是為了日常隱私需求而使用 VPN。NetRiskScan 只將 VPN 狀態視為多個訊號之一，而非單獨的結論。",
    },
    {
      question: "資料中心 IP 代表惡意流量嗎？",
      answer: "不代表。資料中心基礎設施不代表一定是高風險，許多合法服務（包括公共 DNS 解析器）都運行在資料中心 IP 上。",
    },
    {
      question: "NetRiskScan 能檢測住宅代理嗎？",
      answer: "可以。住宅代理是 NetRiskScan 回報的網路類型訊號之一，與 VPN、資料中心、託管等訊號並列，依據 ASN 註冊資訊與網路類型判定，而不只依據 IP 位置。",
    },
    {
      question: "如何檢測一個 IP 是否為 Tor 出口節點？",
      answer: "Tor 出口節點狀態是本頁每次 IP 檢測結果中都會顯示的訊號之一。輸入或自動偵測一個位址後，即可在結果中看到與 VPN、代理、資料中心並列的 Tor 標記。",
    },
    {
      question: "如何檢測 VPS 的 IP 信譽？",
      answer: "你可以像檢測任何位址一樣，在本頁面檢測 VPS 的公開 IP；也可以直接在 VPS 上執行 netriskscan-cli 或呼叫 Developer API，進行自動化、可重複的檢測。",
    },
    {
      question: "不註冊可以使用 NetRiskScan 嗎？",
      answer: "可以。你可以在本頁面直接檢測 IP 位址，無需註冊帳號。",
    },
    {
      question: "NetRiskScan 提供 API 嗎？",
      answer: "提供。NetRiskScan Developer API 可以讓你將 IP 信譽與網路情報直接整合進你的應用程式。",
    },
    {
      question: "有命令列工具（CLI）嗎？",
      answer: "有。netriskscan-cli 是官方命令列用戶端，可透過 npx 直接執行，前幾次檢測無需註冊。",
    },
    {
      question: "有 JavaScript SDK 嗎？",
      answer: "有。@netriskscan/sdk 是面向 Developer API 的型別化 JavaScript/TypeScript 用戶端。",
    },
    {
      question: "結果中的「未知」是什麼意思？",
      answer: "「未知」表示 NetRiskScan 沒有足夠證據確認或排除該位址的某個訊號，這與已確認的負面結果會被區別對待，不會被當作「否」處理。",
    },
  ],
};

const ja: HomeFaqStrings = {
  title: "よくある質問",
  items: [
    {
      question: "IPレピュテーションチェックとは何ですか？",
      answer: "IPレピュテーションチェックは、プロキシ、VPN、Tor、データセンター判定、不正利用履歴、脅威インテリジェンスなどのシグナルをもとに、そのIPアドレスのネットワーク活動がどの程度信頼できるかを示すものです。",
    },
    {
      question: "NetRiskScan 指数とは何を意味しますか？",
      answer: "NetRiskScan 指数は0〜100のスコアで、数値が高いほどネットワークのレピュテーションがクリーンであることを示します。90〜100は「優秀」、75〜89は「良好」、60〜74は「普通」、40〜59は「やや低い」、0〜39は「高リスク」です。",
    },
    {
      question: "VPNのIPは必ず危険ですか？",
      answer: "いいえ。VPNアドレスだからといって必ずしも悪意ある通信を意味するわけではありません。多くの利用者は日常的なプライバシー保護のためにVPNを使用しています。NetRiskScanはVPNの状態を単独の結論としてではなく、複数のシグナルの一つとして扱います。",
    },
    {
      question: "データセンターのIPは悪意ある通信を意味しますか？",
      answer: "いいえ。データセンターのインフラだからといって必ずしも高リスクを意味するわけではありません。公共DNSリゾルバーを含む多くの正規サービスがデータセンターのIPで稼働しています。",
    },
    {
      question: "NetRiskScanはレジデンシャルプロキシを検出できますか？",
      answer: "はい。レジデンシャルプロキシは、VPN、データセンター、ホスティングと並んでNetRiskScanが報告するネットワークタイプのシグナルの一つで、IPの位置情報だけでなくASN登録情報とネットワークタイプ分類に基づいて判定されます。",
    },
    {
      question: "IPがTor出口ノードかどうかはどう確認しますか？",
      answer: "Tor出口ノードのステータスは、このページでのすべてのIPチェック結果に表示されるシグナルの一つです。アドレスを入力または自動検出すると、VPN、プロキシ、データセンターと並んでTorのフラグが表示されます。",
    },
    {
      question: "VPSのIPレピュテーションはどう確認しますか？",
      answer: "このページで通常のアドレスと同じようにVPSのパブリックIPを確認するか、VPS上で直接netriskscan-cliまたはDeveloper APIを実行して、自動化された再現可能なチェックを行うこともできます。",
    },
    {
      question: "アカウントなしでNetRiskScanを利用できますか？",
      answer: "はい。アカウントを作成しなくても、このページでIPアドレスを確認できます。",
    },
    {
      question: "NetRiskScan はAPIを提供していますか？",
      answer: "はい。NetRiskScan Developer APIを使うことで、IPレピュテーションとネットワークインテリジェンスをアプリケーションに直接統合できます。",
    },
    {
      question: "CLIはありますか？",
      answer: "はい。netriskscan-cli は公式のコマンドラインクライアントで、npx から実行できます。最初の数回の確認は登録不要です。",
    },
    {
      question: "JavaScript SDK はありますか？",
      answer: "はい。@netriskscan/sdk はDeveloper API向けの型付きJavaScript/TypeScriptクライアントです。",
    },
    {
      question: "結果に表示される「不明」とは何を意味しますか？",
      answer: "「不明」は、そのアドレスについてシグナルを確認または否定するための十分な証拠がNetRiskScanにないことを意味します。これは確認済みの否定的な結果とは区別して扱われ、「該当なし」とみなされるわけではありません。",
    },
  ],
};

const ko: HomeFaqStrings = {
  title: "자주 묻는 질문",
  items: [
    {
      question: "IP 평판 확인이란 무엇인가요?",
      answer: "IP 평판 확인은 프록시, VPN, Tor, 데이터센터 여부, 악용 신고, 위협 인텔리전스 등의 신호를 바탕으로 해당 IP 주소의 네트워크 활동이 얼마나 신뢰할 수 있는지 보여줍니다.",
    },
    {
      question: "NetRiskScan 지수는 무엇을 의미하나요?",
      answer: "NetRiskScan 지수는 0~100점 척도이며, 값이 높을수록 네트워크 평판이 깨끗함을 의미합니다: 90~100은 매우 우수, 75~89는 우수, 60~74는 보통, 40~59는 낮음, 0~39는 고위험입니다.",
    },
    {
      question: "VPN IP는 항상 위험한가요?",
      answer: "아닙니다. VPN 주소라고 해서 반드시 악성 트래픽을 의미하지는 않습니다. 많은 사용자가 일상적인 개인정보 보호를 위해 VPN을 사용합니다. NetRiskScan은 VPN 상태를 단독 결론이 아니라 여러 신호 중 하나로 취급합니다.",
    },
    {
      question: "데이터센터 IP는 악성 트래픽을 의미하나요?",
      answer: "아닙니다. 데이터센터 인프라라고 해서 반드시 고위험을 의미하지는 않습니다. 공용 DNS 리졸버를 포함한 많은 정상 서비스가 데이터센터 IP에서 운영됩니다.",
    },
    {
      question: "NetRiskScan은 레지덴셜 프록시를 감지할 수 있나요?",
      answer: "네. 레지덴셜 프록시는 VPN, 데이터센터, 호스팅과 함께 NetRiskScan이 보고하는 네트워크 유형 신호 중 하나로, IP 위치뿐 아니라 ASN 등록 정보와 네트워크 유형 분류를 기반으로 판단합니다.",
    },
    {
      question: "IP가 Tor 출구 노드인지 어떻게 확인하나요?",
      answer: "Tor 출구 노드 상태는 이 페이지의 모든 IP 검사 결과에 표시되는 신호 중 하나입니다. 주소를 입력하거나 자동 감지하면 VPN, 프록시, 데이터센터와 함께 Tor 플래그를 확인할 수 있습니다.",
    },
    {
      question: "VPS의 IP 평판은 어떻게 확인하나요?",
      answer: "다른 주소와 마찬가지로 이 페이지에서 VPS의 공인 IP를 확인하거나, VPS에서 직접 netriskscan-cli 또는 Developer API를 실행하여 자동화되고 반복 가능한 검사를 수행할 수 있습니다.",
    },
    {
      question: "계정 없이 NetRiskScan을 사용할 수 있나요?",
      answer: "네. 계정을 만들지 않아도 이 페이지에서 IP 주소를 확인할 수 있습니다.",
    },
    {
      question: "NetRiskScan은 API를 제공하나요?",
      answer: "네. NetRiskScan Developer API를 통해 IP 평판과 네트워크 인텔리전스를 애플리케이션에 직접 통합할 수 있습니다.",
    },
    {
      question: "CLI가 있나요?",
      answer: "네. netriskscan-cli는 공식 명령줄 클라이언트로 npx로 바로 실행할 수 있으며, 첫 몇 번의 확인에는 회원가입이 필요 없습니다.",
    },
    {
      question: "JavaScript SDK가 있나요?",
      answer: "네. @netriskscan/sdk는 Developer API를 위한 타입 지정 JavaScript/TypeScript 클라이언트입니다.",
    },
    {
      question: "결과의 '알 수 없음'은 무엇을 의미하나요?",
      answer: "'알 수 없음'은 해당 주소의 신호를 확인하거나 배제할 증거가 충분하지 않다는 의미입니다. 이는 확인된 부정적 결과와는 다르게 처리되며, 거짓으로 간주되지 않습니다.",
    },
  ],
};

const es: HomeFaqStrings = {
  title: "Preguntas frecuentes",
  items: [
    {
      question: "¿Qué es una comprobación de reputación de IP?",
      answer: "Una comprobación de reputación de IP analiza señales como proxy, VPN, Tor, estado de centro de datos, reportes de abuso e inteligencia de amenazas para describir cuán confiable parece la actividad de red de una dirección IP.",
    },
    {
      question: "¿Qué significa el Índice NetRiskScan?",
      answer: "El Índice NetRiskScan es una puntuación de 0 a 100 donde un valor más alto indica una reputación de red más limpia: 90-100 es Excelente, 75-89 Bueno, 60-74 Aceptable, 40-59 Deficiente y 0-39 Riesgo alto.",
    },
    {
      question: "¿Una IP de VPN siempre es riesgosa?",
      answer: "No. Una dirección de VPN no implica automáticamente tráfico malicioso; muchas personas usan VPN por motivos de privacidad cotidianos. NetRiskScan trata el estado de VPN como una señal más, no como un veredicto por sí solo.",
    },
    {
      question: "¿Una IP de centro de datos significa tráfico malicioso?",
      answer: "No. La infraestructura de centro de datos no implica automáticamente un riesgo alto. Muchos servicios legítimos, incluidos resolutores DNS públicos, funcionan sobre IPs de centro de datos.",
    },
    {
      question: "¿Puede NetRiskScan detectar proxies residenciales?",
      answer: "Sí. El proxy residencial es una de las señales de tipo de red que NetRiskScan reporta junto con VPN, centro de datos y hosting, basada en el registro ASN y la clasificación del tipo de red, no solo en la ubicación de la IP.",
    },
    {
      question: "¿Cómo compruebo si una IP es un nodo de salida Tor?",
      answer: "El estado de nodo de salida Tor es una de las señales que se muestran en cada resultado de comprobación de IP en esta página. Introduce o detecta automáticamente una dirección y busca la marca de Tor junto a VPN, proxy y centro de datos.",
    },
    {
      question: "¿Cómo compruebo la reputación de IP de un VPS?",
      answer: "Comprueba su IP pública en esta página como harías con cualquier dirección, o ejecuta netriskscan-cli o la API de desarrolladores directamente desde el VPS para comprobaciones automatizadas y repetibles.",
    },
    {
      question: "¿Puedo usar NetRiskScan sin una cuenta?",
      answer: "Sí. Puedes comprobar una dirección IP en esta página sin crear una cuenta.",
    },
    {
      question: "¿NetRiskScan ofrece una API?",
      answer: "Sí. La API de desarrolladores de NetRiskScan te permite integrar la reputación de IP y la inteligencia de red directamente en tu aplicación.",
    },
    {
      question: "¿Existe una CLI?",
      answer: "Sí. netriskscan-cli es un cliente oficial de línea de comandos que puedes ejecutar con npx, sin necesidad de registro para tus primeras comprobaciones.",
    },
    {
      question: "¿Existe un SDK de JavaScript?",
      answer: "Sí. @netriskscan/sdk es un cliente tipado de JavaScript/TypeScript para la API de desarrolladores.",
    },
    {
      question: "¿Qué significa \"Desconocido\" en un resultado?",
      answer: "Desconocido significa que NetRiskScan no tiene evidencia suficiente para confirmar o descartar una señal para esa dirección. Se trata de forma distinta a un resultado negativo confirmado; no se asume como falso.",
    },
  ],
};

const de: HomeFaqStrings = {
  title: "Häufig gestellte Fragen",
  items: [
    {
      question: "Was ist eine IP-Reputationsprüfung?",
      answer: "Eine IP-Reputationsprüfung betrachtet Signale wie Proxy, VPN, Tor, Rechenzentrumsstatus, Missbrauchsmeldungen und Threat Intelligence, um zu beschreiben, wie vertrauenswürdig die Netzwerkaktivität einer IP-Adresse erscheint.",
    },
    {
      question: "Was bedeutet der NetRiskScan-Index?",
      answer: "Der NetRiskScan-Index ist eine Skala von 0 bis 100, wobei ein höherer Wert eine sauberere Netzwerkreputation bedeutet: 90-100 Ausgezeichnet, 75-89 Gut, 60-74 Befriedigend, 40-59 Schwach und 0-39 Hohes Risiko.",
    },
    {
      question: "Ist eine VPN-IP immer riskant?",
      answer: "Nein. Eine VPN-Adresse bedeutet nicht automatisch bösartigen Datenverkehr — viele Menschen nutzen VPNs aus alltäglichen Datenschutzgründen. NetRiskScan meldet den VPN-Status als eines von mehreren Signalen, nicht als eigenständiges Urteil.",
    },
    {
      question: "Bedeutet eine Rechenzentrums-IP bösartigen Datenverkehr?",
      answer: "Nein. Rechenzentrums-Infrastruktur bedeutet nicht automatisch ein hohes Risiko. Viele legitime Dienste, darunter öffentliche DNS-Resolver, laufen auf Rechenzentrums-IPs.",
    },
    {
      question: "Kann NetRiskScan residentielle Proxys erkennen?",
      answer: "Ja. Residentieller Proxy ist eines der Netzwerktyp-Signale, die NetRiskScan neben VPN, Rechenzentrum und Hosting meldet — basierend auf ASN-Registrierung und Netzwerktyp-Klassifizierung, nicht nur auf dem IP-Standort.",
    },
    {
      question: "Wie prüfe ich, ob eine IP ein Tor-Exit-Knoten ist?",
      answer: "Der Tor-Exit-Knoten-Status ist eines der Signale, die bei jedem IP-Prüfergebnis auf dieser Seite angezeigt werden. Gib eine Adresse ein oder lass sie automatisch erkennen und achte auf das Tor-Kennzeichen neben VPN, Proxy und Rechenzentrum.",
    },
    {
      question: "Wie prüfe ich die IP-Reputation eines VPS?",
      answer: "Prüfe die öffentliche IP auf dieser Seite wie jede andere Adresse, oder führe netriskscan-cli oder die Developer API direkt auf dem VPS aus, um automatisierte, wiederholbare Prüfungen durchzuführen.",
    },
    {
      question: "Kann ich NetRiskScan ohne Konto nutzen?",
      answer: "Ja. Du kannst eine IP-Adresse auf dieser Seite prüfen, ohne ein Konto zu erstellen.",
    },
    {
      question: "Bietet NetRiskScan eine API?",
      answer: "Ja. Die NetRiskScan Developer API ermöglicht es dir, IP-Reputation und Netzwerkinformationen direkt in deine Anwendung zu integrieren.",
    },
    {
      question: "Gibt es eine CLI?",
      answer: "Ja. netriskscan-cli ist ein offizieller Kommandozeilen-Client, den du mit npx ausführen kannst — für die ersten Prüfungen ist keine Registrierung nötig.",
    },
    {
      question: "Gibt es ein JavaScript-SDK?",
      answer: "Ja. @netriskscan/sdk ist ein typisierter JavaScript/TypeScript-Client für die Developer API.",
    },
    {
      question: "Was bedeutet \"Unbekannt\" in einem Ergebnis?",
      answer: "Unbekannt bedeutet, dass NetRiskScan nicht genügend Nachweise hat, um ein Signal für diese Adresse zu bestätigen oder auszuschließen. Es wird anders behandelt als ein bestätigtes negatives Ergebnis und nicht als falsch angenommen.",
    },
  ],
};

const fr: HomeFaqStrings = {
  title: "Questions fréquentes",
  items: [
    {
      question: "Qu'est-ce qu'une vérification de réputation IP ?",
      answer: "Une vérification de réputation IP examine des signaux tels que le proxy, le VPN, Tor, le statut de centre de données, les signalements d'abus et le renseignement sur les menaces pour décrire à quel point l'activité réseau d'une adresse IP semble fiable.",
    },
    {
      question: "Que signifie l'Indice NetRiskScan ?",
      answer: "L'Indice NetRiskScan est une note de 0 à 100 où une valeur plus élevée signifie une réputation réseau plus propre : 90-100 Excellent, 75-89 Bon, 60-74 Correct, 40-59 Faible et 0-39 Risque élevé.",
    },
    {
      question: "Une IP de VPN est-elle toujours risquée ?",
      answer: "Non. Une adresse VPN ne signifie pas automatiquement un trafic malveillant — de nombreuses personnes utilisent un VPN pour des raisons de confidentialité courantes. NetRiskScan signale le statut VPN comme un signal parmi d'autres, pas comme un verdict à lui seul.",
    },
    {
      question: "Une IP de centre de données signifie-t-elle un trafic malveillant ?",
      answer: "Non. Une infrastructure de centre de données ne signifie pas automatiquement un risque élevé. De nombreux services légitimes, y compris des résolveurs DNS publics, fonctionnent sur des IP de centre de données.",
    },
    {
      question: "NetRiskScan peut-il détecter les proxys résidentiels ?",
      answer: "Oui. Le proxy résidentiel est l'un des signaux de type de réseau que NetRiskScan signale aux côtés du VPN, du centre de données et de l'hébergement, basé sur l'enregistrement ASN et la classification du type de réseau, pas seulement sur la localisation de l'IP.",
    },
    {
      question: "Comment vérifier si une IP est un nœud de sortie Tor ?",
      answer: "Le statut de nœud de sortie Tor est l'un des signaux affichés dans chaque résultat de vérification IP sur cette page. Saisissez ou détectez automatiquement une adresse et repérez l'indicateur Tor aux côtés du VPN, du proxy et du centre de données.",
    },
    {
      question: "Comment vérifier la réputation IP d'un VPS ?",
      answer: "Vérifiez son IP publique sur cette page comme pour n'importe quelle adresse, ou exécutez netriskscan-cli ou l'API Developer directement depuis le VPS pour des vérifications automatisées et reproductibles.",
    },
    {
      question: "Puis-je utiliser NetRiskScan sans compte ?",
      answer: "Oui. Vous pouvez vérifier une adresse IP sur cette page sans créer de compte.",
    },
    {
      question: "NetRiskScan propose-t-il une API ?",
      answer: "Oui. L'API Developer de NetRiskScan vous permet d'intégrer la réputation IP et le renseignement réseau directement dans votre application.",
    },
    {
      question: "Existe-t-il une CLI ?",
      answer: "Oui. netriskscan-cli est un client officiel en ligne de commande exécutable via npx, sans inscription requise pour vos premières vérifications.",
    },
    {
      question: "Existe-t-il un SDK JavaScript ?",
      answer: "Oui. @netriskscan/sdk est un client JavaScript/TypeScript typé pour l'API Developer.",
    },
    {
      question: "Que signifie « Inconnu » dans un résultat ?",
      answer: "Inconnu signifie que NetRiskScan ne dispose pas de preuves suffisantes pour confirmer ou écarter un signal pour cette adresse. Ce cas est traité différemment d'un résultat négatif confirmé et n'est pas considéré comme faux.",
    },
  ],
};

const ptBr: HomeFaqStrings = {
  title: "Perguntas frequentes",
  items: [
    {
      question: "O que é uma verificação de reputação de IP?",
      answer: "Uma verificação de reputação de IP analisa sinais como proxy, VPN, Tor, status de datacenter, denúncias de abuso e inteligência de ameaças para descrever quão confiável parece a atividade de rede de um endereço IP.",
    },
    {
      question: "O que significa o Índice NetRiskScan?",
      answer: "O Índice NetRiskScan é uma pontuação de 0 a 100, em que um valor mais alto indica uma reputação de rede mais limpa: 90-100 é Excelente, 75-89 Bom, 60-74 Regular, 40-59 Fraco e 0-39 Alto risco.",
    },
    {
      question: "Um IP de VPN é sempre arriscado?",
      answer: "Não. Um endereço de VPN não significa automaticamente tráfego malicioso — muitas pessoas usam VPN por motivos comuns de privacidade. O NetRiskScan trata o status de VPN como mais um sinal entre vários, não como um veredito isolado.",
    },
    {
      question: "Um IP de datacenter significa tráfego malicioso?",
      answer: "Não. Infraestrutura de datacenter não significa automaticamente alto risco. Muitos serviços legítimos, incluindo resolvedores DNS públicos, funcionam em IPs de datacenter.",
    },
    {
      question: "O NetRiskScan consegue detectar proxies residenciais?",
      answer: "Sim. Proxy residencial é um dos sinais de tipo de rede que o NetRiskScan reporta junto com VPN, datacenter e hospedagem, com base no registro de ASN e na classificação do tipo de rede, não apenas na localização do IP.",
    },
    {
      question: "Como verifico se um IP é um nó de saída Tor?",
      answer: "O status de nó de saída Tor é um dos sinais mostrados em todo resultado de verificação de IP nesta página. Informe ou detecte automaticamente um endereço e procure o sinalizador Tor ao lado de VPN, proxy e datacenter.",
    },
    {
      question: "Como verifico a reputação de IP de um VPS?",
      answer: "Verifique o IP público dele nesta página como faria com qualquer endereço, ou execute o netriskscan-cli ou a API de desenvolvedor diretamente no VPS para verificações automatizadas e repetíveis.",
    },
    {
      question: "Posso usar o NetRiskScan sem uma conta?",
      answer: "Sim. Você pode verificar um endereço IP nesta página sem criar uma conta.",
    },
    {
      question: "O NetRiskScan oferece uma API?",
      answer: "Sim. A API de desenvolvedor do NetRiskScan permite integrar a reputação de IP e a inteligência de rede diretamente na sua aplicação.",
    },
    {
      question: "Existe uma CLI?",
      answer: "Sim. netriskscan-cli é um cliente oficial de linha de comando que você pode executar com npx, sem necessidade de cadastro para suas primeiras verificações.",
    },
    {
      question: "Existe um SDK JavaScript?",
      answer: "Sim. @netriskscan/sdk é um cliente JavaScript/TypeScript tipado para a API de desenvolvedor.",
    },
    {
      question: "O que significa \"Desconhecido\" em um resultado?",
      answer: "Desconhecido significa que o NetRiskScan não tem evidências suficientes para confirmar ou descartar um sinal para esse endereço. Isso é tratado de forma diferente de um resultado negativo confirmado; não é assumido como falso.",
    },
  ],
};

const ru: HomeFaqStrings = {
  title: "Часто задаваемые вопросы",
  items: [
    {
      question: "Что такое проверка репутации IP?",
      answer: "Проверка репутации IP анализирует такие сигналы, как прокси, VPN, Tor, статус дата-центра, жалобы на злоупотребления и данные об угрозах, чтобы показать, насколько заслуживает доверия сетевая активность IP-адреса.",
    },
    {
      question: "Что означает индекс NetRiskScan?",
      answer: "Индекс NetRiskScan — это шкала от 0 до 100, где более высокое значение означает более чистую репутацию сети: 90-100 — отлично, 75-89 — хорошо, 60-74 — удовлетворительно, 40-59 — слабо, 0-39 — высокий риск.",
    },
    {
      question: "IP-адрес VPN всегда опасен?",
      answer: "Нет. Адрес VPN не означает автоматически вредоносный трафик — многие люди используют VPN по обычным причинам конфиденциальности. NetRiskScan рассматривает статус VPN как один из нескольких сигналов, а не как самостоятельный вердикт.",
    },
    {
      question: "Означает ли IP дата-центра вредоносный трафик?",
      answer: "Нет. Инфраструктура дата-центра не означает автоматически высокий риск. Многие легитимные сервисы, включая публичные DNS-резолверы, работают на IP дата-центров.",
    },
    {
      question: "Может ли NetRiskScan обнаруживать резидентные прокси?",
      answer: "Да. Резидентный прокси — один из сигналов типа сети, которые NetRiskScan сообщает наряду с VPN, дата-центром и хостингом, на основе регистрации ASN и классификации типа сети, а не только местоположения IP.",
    },
    {
      question: "Как проверить, является ли IP выходным узлом Tor?",
      answer: "Статус выходного узла Tor — один из сигналов, отображаемых в каждом результате проверки IP на этой странице. Введите адрес или используйте автоопределение и посмотрите на отметку Tor рядом с VPN, прокси и дата-центром.",
    },
    {
      question: "Как проверить репутацию IP-адреса VPS?",
      answer: "Проверьте его публичный IP на этой странице так же, как и любой другой адрес, или запустите netriskscan-cli либо Developer API прямо на VPS для автоматизированных, повторяемых проверок.",
    },
    {
      question: "Можно ли использовать NetRiskScan без аккаунта?",
      answer: "Да. Вы можете проверить IP-адрес на этой странице без создания аккаунта.",
    },
    {
      question: "Предоставляет ли NetRiskScan API?",
      answer: "Да. Developer API NetRiskScan позволяет интегрировать репутацию IP и сетевую аналитику прямо в ваше приложение.",
    },
    {
      question: "Есть ли CLI?",
      answer: "Да. netriskscan-cli — официальный клиент командной строки, который можно запустить через npx; для первых проверок регистрация не требуется.",
    },
    {
      question: "Есть ли JavaScript SDK?",
      answer: "Да. @netriskscan/sdk — типизированный клиент JavaScript/TypeScript для Developer API.",
    },
    {
      question: "Что означает «Неизвестно» в результате?",
      answer: "«Неизвестно» означает, что у NetRiskScan недостаточно данных, чтобы подтвердить или исключить сигнал для этого адреса. Это трактуется иначе, чем подтверждённый отрицательный результат, и не считается «нет».",
    },
  ],
};

const dictionaries: Record<string, HomeFaqStrings> = {
  en,
  "zh-cn": zhCn,
  "zh-tw": zhTw,
  ja,
  ko,
  es,
  de,
  fr,
  "pt-br": ptBr,
  ru,
};

export function getHomeFaqStrings(languageCode: string): HomeFaqStrings {
  return dictionaries[languageCode.toLowerCase()] ?? en;
}
